import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Stack,
  TextInput,
} from 'component-library';
import { FC, PropsWithChildren, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CustomBenchmark, CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from '@/components/Form';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import {
  useCreateCustomBenchmark,
  useRemoveCustomBenchmark,
  useUpdateCustomBenchmark,
} from '@/hooks/useUpdateCustomBenchmarks';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { isNullish } from '@/utils/booleans';

type CustomBenchmarkDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  indicatorType: string;
};

type DialogView = 'LIST' | 'CREATE_EDIT';

const BenchmarkValueInput = ({
  form,
  name,
  label,
}: {
  form: UseFormReturn<CustomBenchmark>;
  name: 'low_value' | 'moderate_value' | 'high_value';
  label: string;
}) => {
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className='flex flex-col gap-1'>
          <CustomFormLabel>{label}</CustomFormLabel>
          <div className='flex flex-row items-center gap-2'>
            <div className='relative flex-grow'>
              <FormControl>
                <TextInput type='number' className='pr-12 w-full' {...field} />
              </FormControl>
              <div className='absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1'>
                <span className='text-text-secondary text-sm pointer-events-none'>
                  {t('graph.benchmark.form.unit')}
                </span>
                <button
                  type='button'
                  onClick={() => form.setValue(name, undefined, { shouldDirty: true })}
                  className='p-2 text-text-secondary hover:text-error transition-colors'
                  aria-label={`Clear ${label}`}
                >
                  <i className='ri-close-line text-xl' />
                </button>
              </div>
            </div>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const CustomBenchmarkForm = ({
  organizationId,
  indicatorType,
  editingBenchmark,
  onCancel,
  onSuccess,
}: {
  organizationId: string;
  indicatorType: string;
  editingBenchmark?: CustomBenchmarkSuccessResponse | null;
  onCancel: () => void;
  onSuccess: () => void;
}) => {
  const { t } = useTranslation();

  const { submit: createBenchmark, isSubmitting: isCreating } = useCreateCustomBenchmark();
  const { submit: updateBenchmark, isSubmitting: isUpdating } = useUpdateCustomBenchmark();

  const isEditMode = !!editingBenchmark;
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<CustomBenchmark>({
    defaultValues: {
      name: editingBenchmark?.name || '',
      low_value: editingBenchmark?.low_value,
      moderate_value: editingBenchmark?.moderate_value,
      high_value: editingBenchmark?.high_value,
      notes: editingBenchmark?.notes || '',
    },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    const hasAtLeastOneValue =
      !isNullish(data.low_value) || !isNullish(data.moderate_value) || !isNullish(data.high_value);

    if (!hasAtLeastOneValue) {
      form.setError('high_value', {
        type: 'manual',
        message: t('graph.benchmark.form.validation.minOneValue'),
      });
      return;
    }

    form.clearErrors('high_value');
    form.clearErrors('root');

    const payload = isEditMode ? { ...editingBenchmark, ...data } : { ...data, indicator_name: indicatorType || '' };

    const handleError = () => {
      form.setError('root', {
        type: 'manual',
        message: t('graph.benchmark.form.submitError'),
      });
    };

    const submitAction = isEditMode ? updateBenchmark : createBenchmark;

    await submitAction(organizationId, isEditMode ? editingBenchmark?.id : undefined, payload, onSuccess, handleError);
  });

  const isKnownBenchmark = indicatorType && Object.values(GraphTileType).includes(indicatorType as GraphTileType);

  return (
    <Form {...form}>
      <form id='benchmark-form' onSubmit={onSubmit} className='flex flex-col gap-5'>
        <DialogBody className='py-4 overflow-y-auto max-h-[60vh] pr-2'>
          <FormField
            control={form.control}
            name='name'
            rules={{ required: t('global.ui.form.input.required') }}
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <CustomFormLabel>
                  {isKnownBenchmark
                    ? t('graph.benchmark.form.name.label', {
                        indicator: t(`global.compare.graphs.indicators.${indicatorType as GraphTileType}`),
                      })
                    : t('global.ui.labels.benchmark')}
                </CustomFormLabel>
                <FormControl>
                  <TextInput {...field} />
                </FormControl>
                <FormDescription>{t('graph.benchmark.form.name.helper')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <BenchmarkValueInput form={form} name='low_value' label={t('graph.benchmark.form.lowValue.label')} />

          <BenchmarkValueInput
            form={form}
            name='moderate_value'
            label={t('graph.benchmark.form.moderateValue.label')}
          />

          <BenchmarkValueInput form={form} name='high_value' label={t('graph.benchmark.form.highValue.label')} />

          <FormField
            control={form.control}
            name='notes'
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <CustomFormLabel>{t('graph.benchmark.form.notes.label')}</CustomFormLabel>
                <FormControl>
                  <TextInput {...field} />
                </FormControl>
                <FormDescription>{t('graph.benchmark.form.notes.helper')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.formState.errors.root && (
            <div className='mt-2 p-3 bg-error/10 border border-error/20 rounded-md'>
              <p className='text-error text-sm font-medium'>{form.formState.errors.root.message}</p>
            </div>
          )}
        </DialogBody>

        <DialogFooter className='pt-4 flex-row justify-end'>
          <Button variant='text' type='button' onClick={onCancel}>
            {t('global.ui.buttons.cancel')}
          </Button>
          <Button type='submit' loading={isSubmitting}>
            {t('global.ui.buttons.apply')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export const CustomBenchmarkDialog = ({ isOpen, onClose, indicatorType }: CustomBenchmarkDialogProps) => {
  const { t } = useTranslation();

  const organizationId = useOrganizationId();

  const [currentView, setCurrentView] = useState<DialogView>('LIST');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [editingBenchmark, setEditingBenchmark] = useState<CustomBenchmarkSuccessResponse | null>(null);
  const { data: response } = useGetCustomBenchmarks(organizationId, indicatorType);
  const { submit: removeBenchmark } = useRemoveCustomBenchmark();

  const benchmarks = response?.results || [];

  const handleClose = (open: boolean) => {
    if (!open) {
      setCurrentView('LIST');
      setDeleteError(null);
      onClose();
    }
  };

  const handleCreateNew = () => {
    setEditingBenchmark(null);
    setDeleteError(null);
    setCurrentView('CREATE_EDIT');
  };

  const handleDelete = (benchmarkId: string) => {
    setDeleteError(null);
    removeBenchmark(organizationId, benchmarkId, undefined, () => {
      setDeleteError(t('graph.benchmark.dialog.deleteError'));
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='max-w-xl'>
        {currentView === 'LIST' && (
          <>
            <DialogHeader className='mb-4'>
              <DialogTitle className='typography-h3 text-left'>{t('graph.benchmark.dialog.listTitle')}</DialogTitle>
            </DialogHeader>

            <div className='h-[1px] bg-divider -mx-12' />

            <DialogBody className='py-6 min-h-[200px] flex flex-col overflow-y-auto max-h-[60vh] pr-2'>
              {deleteError && (
                <div className='mb-4 p-3 bg-error/10 border border-error/20 rounded-md'>
                  <p className='text-error text-sm font-medium'>{deleteError}</p>
                </div>
              )}
              {benchmarks && benchmarks.length > 0 ? (
                <div className='flex flex-col gap-3'>
                  {benchmarks.map((benchmark) => (
                    <Stack key={benchmark.id} direction='row' centerMain className='justify-between p-2'>
                      <span className='font-semibold text-text-primary'>{benchmark.name}</span>
                      <Stack direction='row' spacing={4}>
                        <Button
                          variant='outline'
                          onClick={() => {
                            setEditingBenchmark(benchmark);
                            setCurrentView('CREATE_EDIT');
                          }}
                          className='w-11 h-11 rounded-2xl'
                          aria-label='Edit benchmark'
                        >
                          <i className='ri-pencil-line text-lg' />
                        </Button>

                        <Button
                          variant='outline'
                          onClick={() => handleDelete(benchmark.id)}
                          className='w-11 h-11 rounded-2xl hover:bg-error hover:border-error'
                        >
                          <i className='ri-delete-bin-line text-lg' />
                        </Button>
                      </Stack>
                    </Stack>
                  ))}
                </div>
              ) : (
                <div>
                  <p className='text-text-secondary text-sm'>{t('graph.benchmark.dialog.noBenchmarks')}</p>
                </div>
              )}
              <Stack spacing={6} className='mt-auto pt-6'>
                <Button variant='outline' onClick={handleCreateNew}>
                  {t('graph.benchmark.dialog.newBenchmarkButtonLabel')}
                </Button>
              </Stack>
            </DialogBody>

            <DialogFooter className='flex-row justify-end'>
              <Button variant='text' onClick={() => onClose()}>
                {t('global.ui.buttons.cancel')}
              </Button>
              <Button
                onClick={() => {
                  onClose();
                }}
              >
                {t('global.ui.buttons.save')}
              </Button>
            </DialogFooter>
          </>
        )}

        {currentView === 'CREATE_EDIT' && (
          <>
            <DialogHeader className='mb-4 flex flex-row items-center gap-2 text-left'>
              <button
                onClick={() => setCurrentView('LIST')}
                className='text-active-54 hover:text-primary-100'
                data-testid='dialog-back-btn'
              >
                <i className='ri-arrow-left-line text-xl' />
              </button>
              <DialogTitle className='typography-h3 text-left'>
                {editingBenchmark
                  ? t('graph.benchmark.dialog.editBenchmarkTitle')
                  : t('graph.benchmark.dialog.createBenchmarkTitle')}
              </DialogTitle>
            </DialogHeader>

            <CustomBenchmarkForm
              organizationId={organizationId}
              indicatorType={indicatorType}
              editingBenchmark={editingBenchmark}
              onCancel={() => setCurrentView('LIST')}
              onSuccess={() => setCurrentView('LIST')}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CustomFormLabel: FC<PropsWithChildren> = ({ children }) => {
  const { error } = useFormField();

  return (
    <FormLabel
      className={cn('text-[.75rem] font-medium leading-4 text-text-secondary md:text-[.8125rem] md:leading-5', {
        'text-error': error,
      })}
    >
      {children}
    </FormLabel>
  );
};
